import { db, storage, FIREBASE_DATABASE_URL } from 'utils/firebase-config';
import { ref as dbRef, onValue, set, update, remove, get, off, serverTimestamp, increment } from 'firebase/database';
import { ref as storageRef, getDownloadURL, uploadBytesResumable, listAll, deleteObject } from 'firebase/storage';
import { useEffect, useState, useRef, useCallback, useMemo, useReducer } from 'react';
import { subscribeMessages, getRoomEntry, loadOlderMessagesForRoom } from './messagesStore';
import { attachmentPath, resolveAttachmentUrl, invalidateAttachmentUrl } from './attachmentUrlCache';
import moment from 'moment';
import { getDistance, getMessageTime, normalizeDistance, sortRoomsByDistance, getParticipantsArray, mergeParticipantsByPk, isPublicRoom, isWelcomePublicTopic, pickCanonicalPublicRoom } from 'helpers/utils';
import { api } from './conversation.service';
import { validateCurrentUserForFirebase } from 'utils/authUtils';
import { markJoinRequestPending } from 'utils/pendingJoinRequests';
import { toast } from 'react-toastify';
import { MAX_INTRO_VIDEO_SIZE } from 'store/constants';

/**
 * Middleware for Firebase write operations
 * Automatically validates current user and shows toast on error
 */
const withFirebaseUpdate = <T extends (...args: any[]) => Promise<any>>(
  fn: T
): T => {
  return (async (...args: any[]) => {
    try {
      // Validate current user before operation
      validateCurrentUserForFirebase();
      // Call the original function
      return await fn(...args);
    } catch (error: any) {
      // If validation fails, show toast and throw special error
      if (error.message && error.message.includes('INVALID_USER_DATA')) {
        toast.error('User data is missing or invalid. Please refresh and try again.');
        // Throw a special error that components can check for
        const userDataError: any = new Error('INVALID_USER_DATA');
        userDataError.code = 'INVALID_USER_DATA';
        throw userDataError;
      }
      // Re-throw other errors for components to handle
      throw error;
    }
  }) as T;
};
// import Interval from '../../utils/Interval';

// export const createRoom = async (room: any) => {
//   const createRoom: any = ref(db, `rooms/${room.roomId}`);
//   try {
//     await set(createRoom, {
//       roomId: room.roomId,
//       topic: room.topic,
//       host: {
//         id: room.host.pk,
//         name: room.host.name,
//         isSuperHost: room.host.is_super_host,
//         image: room.host.profile,
//       },
//       created_at: room.created_at,
//       isDeleted: false,
//       call_status: false,
//     });
//   } catch (err) {
//     console.log(err);
//   }
// };

const mapHostToFirebase = (host: any) => ({
  pk: host.pk,
  name: host.name,
  email: host.email,
  role: host.role,
  profile: host.profile,
  latitude: host.latitude,
  longitude: host.longitude,
  silver_coin: host.silver_coin,
  gold_coin: host.gold_coin,
  is_super_host: host.is_super_host,
});

/** Normalize a Django API room into the shape written to Firebase `rooms/{pk}`. */
export const apiRoomToFirebasePayload = (room: any) => ({
  pk: room.pk,
  topic: room.topic,
  host: mapHostToFirebase(room.host),
  participants: room.participants || [],
  latitude: room.latitude === null || room.latitude === undefined ? 'null' : room.latitude,
  longitude: room.longitude === null || room.longitude === undefined ? 'null' : room.longitude,
  created_at: room.created_at,
  is_deleted: room.is_deleted ?? false,
  is_public: room.is_public ?? false,
  status: room.status ?? 'CHAT',
  intro_video_url: room.intro_video_url || null,
});

export const createRoom = withFirebaseUpdate(async (room: any) => {
  if (!room?.pk || !room?.host?.pk) {
    throw new Error('Cannot create Firebase room without pk and host');
  }

  const createRoom: any = dbRef(db, `rooms/${room.pk}`);
  const { host } = room;
  let timeStamp = new Date().toString()

  // Get location data for the room
  let roomCity = "Unknown";
  let roomCountry = "Unknown";
  
  if (room.latitude && room.longitude && room.latitude !== "null" && room.longitude !== "null") {
    try {
      // Import API function for geocoding
      const { api } = await import('./conversation.service');
      const locationResponse = await api.getLocationFromCoordinates({
        latitude: room.latitude.toString(),
        longitude: room.longitude.toString()
      });
      
      if (locationResponse && locationResponse.city && locationResponse.country) {
        roomCity = locationResponse.city;
        roomCountry = locationResponse.country;
      }
    } catch (error) {
      console.warn('Failed to get location for room:', error);
      // Continue with "Unknown" values
    }
  }

  try {
    await set(createRoom, {
      timeStamp: timeStamp,
      pk: room.pk,
      topic: room.topic,
      host: mapHostToFirebase(host),
      participants: room.participants,
      latitude: room.latitude === null ? "null" : room.latitude,
      longitude: room.longitude === null ? "null" : room.longitude,
      city: roomCity,        // NEW: Add city field
      country: roomCountry,  // NEW: Add country field
      created_at: room.created_at,
      is_deleted: room.is_deleted,
      status: room.status,
      intro_video_url: room.intro_video_url || null,
      call_active: false,
    });
  } catch (err) {
    console.log(err);
  }
});

export const updateRoomStatus = withFirebaseUpdate(async (data: { id: number | string; status: string; call_active?: boolean }) => {
  const roomRef = dbRef(db, `rooms/${data.id}`);
  const patch: Record<string, unknown> = { status: data.status };

  if (data.status === 'CHAT') {
    patch.call_active = false;
    patch.call_participant_count = 0;
  } else if (data.status === 'VIDEO') {
    patch.call_active = data.call_active ?? false;
  } else if (data.call_active !== undefined) {
    patch.call_active = data.call_active;
  }

  try {
    await update(roomRef, patch);
  } catch (err) {
    console.log(err);
    throw err;
  }
});

/** Host clicked Start Video — room is VIDEO but not live until host joins Jitsi. */
export const beginVideoCall = withFirebaseUpdate(async (roomId: number | string) => {
  await update(dbRef(db, `rooms/${roomId}`), {
    status: 'VIDEO',
    call_active: false,
    call_participant_count: 0,
  });
});

export const setCallActive = withFirebaseUpdate(async (roomId: number | string, active: boolean) => {
  const patch: Record<string, unknown> = { call_active: active };
  if (!active) {
    patch.call_participant_count = 0;
  }
  await update(dbRef(db, `rooms/${roomId}`), patch);
  if (!active) {
    try {
      await remove(dbRef(db, `call_join_requests/${roomId}`));
    } catch {
      // best-effort
    }
  }
});

/** Live Jitsi headcount — readable on prejoin before External API has a conference. */
export const setCallParticipantCount = withFirebaseUpdate(async (roomId: number | string, count: number) => {
  const safe = Number.isFinite(count) ? Math.max(0, Math.floor(count)) : 0;
  await update(dbRef(db, `rooms/${roomId}`), { call_participant_count: safe });
});

/** Subscribe to live call size for /call header (esp. prejoin). Returns unsubscribe. */
export const subscribeCallParticipantCount = (
  roomId: number | string,
  onCount: (count: number) => void
): (() => void) => {
  const countRef = dbRef(db, `rooms/${roomId}/call_participant_count`);
  return onValue(countRef, (snapshot) => {
    const val = snapshot.val();
    onCount(typeof val === 'number' && Number.isFinite(val) ? Math.max(0, val) : 0);
  });
};

/** Subscribe to whether the host's live call is still active. Returns unsubscribe. */
export const subscribeCallActive = (
  roomId: number | string,
  onActive: (active: boolean, status: string | null) => void
): (() => void) => {
  const roomRef = dbRef(db, `rooms/${roomId}`);
  return onValue(roomRef, (snapshot) => {
    const val = snapshot.val();
    if (!val) {
      onActive(false, null);
      return;
    }
    const status = typeof val.status === 'string' ? val.status : null;
    const active = val.call_active === true && status !== 'CHAT';
    onActive(active, status);
  });
};

/** Host presence while in Jitsi — used to clear abandoned VIDEO rooms. */
export const CALL_HEARTBEAT_STALE_MS = 10 * 60 * 1000; // 10 minutes
export const CALL_HEARTBEAT_INTERVAL_MS = 300 * 1000; // 300 seconds

export const touchCallHeartbeat = withFirebaseUpdate(async (roomId: number | string) => {
  await update(dbRef(db, `rooms/${roomId}`), { call_heartbeat: Date.now() });
});

/**
 * End video via Firebase REST + keepalive so tab/window close can still land the write.
 * Browsers often cancel async SDK updates during beforeunload/pagehide.
 */
export const endVideoCallKeepalive = (roomId: number | string): boolean => {
  if (roomId == null || roomId === '') return false;
  const url = `${FIREBASE_DATABASE_URL}/rooms/${roomId}.json`;
  const body = JSON.stringify({
    status: 'CHAT',
    call_active: false,
    call_heartbeat: null,
    call_participant_count: 0,
  });

  try {
    if (typeof fetch === 'function') {
      void fetch(url, {
        method: 'PATCH',
        body,
        headers: { 'Content-Type': 'application/json' },
        keepalive: true,
      });
      // Best-effort clear join queue (separate path)
      void fetch(`${FIREBASE_DATABASE_URL}/call_join_requests/${roomId}.json`, {
        method: 'DELETE',
        keepalive: true,
      });
      return true;
    }
  } catch (err) {
    console.error('endVideoCallKeepalive failed:', err);
  }
  return false;
};

/** End video session — room returns to chat / NEARBY panel. */
export const endVideoCall = withFirebaseUpdate(async (roomId: number | string) => {
  await updateRoomStatus({ id: roomId, status: 'CHAT', call_active: false });
  try {
    await update(dbRef(db, `rooms/${roomId}`), {
      call_heartbeat: null,
      call_participant_count: 0,
    });
  } catch {
    // best-effort clear
  }
  try {
    await remove(dbRef(db, `call_join_requests/${roomId}`));
  } catch {
    // best-effort
  }
});

/**
 * Clear rooms stuck in VIDEO / call_active after the host tab-closed or crashed.
 * Safe to run whenever the room list is loaded.
 */
export const clearStaleVideoCalls = withFirebaseUpdate(async (rooms?: any[]) => {
  let list = rooms;
  if (!Array.isArray(list) || list.length === 0) {
    const snapshot = await get(dbRef(db, 'rooms'));
    list = [];
    if (snapshot.exists()) {
      snapshot.forEach((doc: any) => {
        const data = doc.val();
        if (data && data.is_deleted !== true) list!.push(data);
      });
    }
  }

  const now = Date.now();
  const stale = list.filter((r: any) => {
    if (!r || r.is_deleted === true) return false;
    const beat = typeof r.call_heartbeat === 'number' ? r.call_heartbeat : 0;
    const beatStale = !beat || now - beat > CALL_HEARTBEAT_STALE_MS;

    // Live flag left on after host vanished.
    if (r.call_active === true && beatStale) return true;
    // Was live at some point (heartbeat exists) but host gone long enough.
    if (r.status === 'VIDEO' && beat > 0 && now - beat > CALL_HEARTBEAT_STALE_MS) return true;
    return false;
  });

  await Promise.all(
    stale.map(async (r: any) => {
      try {
        await update(dbRef(db, `rooms/${r.pk}`), {
          status: 'CHAT',
          call_active: false,
          call_heartbeat: null,
          call_participant_count: 0,
        });
      } catch (err) {
        console.error('Failed to clear stale video room', r.pk, err);
      }
    })
  );

  return stale.length;
});

/**
 * Upsert one Django room into Firebase. Creates missing rooms; updates Django-owned
 * fields on existing rooms while preserving live `call_active` when appropriate.
 */
export const syncRoomFromApiToFirebase = withFirebaseUpdate(async (room: any) => {
  if (!room?.pk || !room?.host?.pk) {
    return;
  }

  const roomRef = dbRef(db, `rooms/${room.pk}`);
  const snapshot = await get(roomRef);

  if (!snapshot.exists()) {
    await createRoom(apiRoomToFirebasePayload(room));
    return;
  }

  const existing = snapshot.val();
  const status = room.status ?? 'CHAT';

  // Per-user read markers live only in Firebase (never in the API payload). Preserve them
  // across the sync, or every nearby-rooms refresh would reset unread tracking.
  const preserveSeen = (target: any, prev: any) => {
    if (!prev) return target;
    const out = { ...target };
    if (prev.lastSeenMessage !== undefined) out.lastSeenMessage = prev.lastSeenMessage;
    if (prev.lastSeenTime !== undefined) out.lastSeenTime = prev.lastSeenTime;
    if (prev.lastSeenCount !== undefined) out.lastSeenCount = prev.lastSeenCount;
    if (prev.lastNotifiedSeenMessage !== undefined) {
      out.lastNotifiedSeenMessage = prev.lastNotifiedSeenMessage;
    }
    if (prev.lastEmailSentAt !== undefined) out.lastEmailSentAt = prev.lastEmailSentAt;
    if (prev.lastEmailedMessageId !== undefined) out.lastEmailedMessageId = prev.lastEmailedMessageId;
    return out;
  };

  const apiPayload = apiRoomToFirebasePayload(room);
  const existingByPk = new Map(
    getParticipantsArray(existing.participants).map((p: any) => [p?.pk, p])
  );
  const mergedParticipants = mergeParticipantsByPk(existing.participants, room.participants).map(
    (p: any) => preserveSeen(p, existingByPk.get(p?.pk))
  );

  const patch: Record<string, unknown> = {
    ...apiPayload,
    host: preserveSeen(apiPayload.host, existing.host),
    participants: mergedParticipants,
  };

  if (room.is_deleted || status === 'CHAT') {
    patch.call_active = false;
  } else if (status === 'VIDEO' && existing.call_active === undefined) {
    patch.call_active = false;
  }

  await update(roomRef, patch);
});

/** Sync all active nearby Django rooms into Firebase after API fetch. */
export const syncRoomsFromApiToFirebase = withFirebaseUpdate(async (rooms: any[]) => {
  if (!Array.isArray(rooms)) {
    return;
  }

  for (const room of rooms) {
    if (room?.pk && room?.host?.pk && room.is_deleted !== true) {
      await syncRoomFromApiToFirebase(room);
    }
  }
});

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export interface FetchVideoRoomOptions {
  /** Host just clicked Start — retry until Firebase status is VIDEO. */
  isStartingVideo?: boolean;
  /** Guest join — require host to be in Jitsi (call_active). */
  requireCallActive?: boolean;
}

export const fetchVideoRoomFromFirebase = async (
  roomId: number | string,
  options: FetchVideoRoomOptions = {}
): Promise<any> => {
  const maxAttempts = options.isStartingVideo || options.requireCallActive ? 12 : 1;
  const delayMs = 400;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const snap = await get(dbRef(db, `rooms/${roomId}`));
    if (!snap.exists()) {
      throw new Error('Room not found');
    }

    const roomRaw = snap.val();

    if (options.requireCallActive && !roomRaw.call_active) {
      if (attempt < maxAttempts - 1) {
        await sleep(delayMs);
        continue;
      }
      throw new Error('The host has not started the video call yet.');
    }

    if (roomRaw?.status !== 'VIDEO') {
      if (options.isStartingVideo && attempt < maxAttempts - 1) {
        await sleep(delayMs);
        continue;
      }
      throw new Error('The video call is not available.');
    }

    return roomRaw;
  }

  throw new Error('The video call is not available.');
};

export const updateRoomIntroVideoInFirebase = withFirebaseUpdate(async (roomId: string, introVideoUrl: string | null) => {
  const roomRef = dbRef(db, `rooms/${roomId}`);
  
  try {
    // Get current room data to ensure room exists
    const snapshot = await get(roomRef);
    const currentRoomData = snapshot.val();
    
    if (currentRoomData) {
      // Update only the intro_video_url field
      await update(roomRef, {
        intro_video_url: introVideoUrl,
      });
    } else {
      console.warn(`Room ${roomId} not found in Firebase`);
    }
  } catch (err) {
    console.error('Error updating intro video in Firebase:', err);
    throw err;
  }
});

export const updateMessageStatus = withFirebaseUpdate(async (room: any) => {
  const getRoom: any = dbRef(db, `chats/${room.roomId}/messages/${room.messageId}`)
  let pin = room.pin
  try {
    await update(getRoom, {
      pin: pin,
      timeStamp: room.timeStamp,
    });
  } catch (err) {
    console.log(err);
    throw err;
  }
});

export const updateNewRoomStatus = withFirebaseUpdate(async (room: any) => {
  const roomPk = room?.room?.pk;
  if (roomPk == null) {
    return;
  }
  const getRoom: any = dbRef(db, `rooms/${roomPk}`);
  try {
    await update(getRoom, {
      activeRoom: room.activeRoom,
    });
  } catch (err) {
    console.log(err);
    throw err;
  }
});

export const emailNotifications = withFirebaseUpdate(async (data: any) => {
  const createPinUsers: any = dbRef(db, `emailnotifications/${data.pk}`);
  try {
    await update(createPinUsers
      , {
        pk: data.pk,
        email: data.email,
        checked: data.checked
      }
    );
  } catch (err) {
    console.log(err);
    throw err;
  }
});

export const getEmailNotifications = () => {
  const getRoom: any = dbRef(db,`emailnotifications/${user.pk}`);
  let emailnotifications;
  onValue(getRoom, (querySnapshot: any) => {
    emailnotifications = querySnapshot.val()
  });
  return emailnotifications;
};

export const getRoomStatus = (roomId: any) => {
  
  // const getRoom: any = ref(db, `rooms/${roomId}`);
  // let aaaa = onValue(getRoom, (querySnapshot: any) => {
  //   console.log("~~~~~~~~~~~~~~~", querySnapshot);
    
  //   return querySnapshot.val()
  // });
  // console.log("0-0-0-0-0", aaaa);
  
  // const [room, setRoom] = useState<any>({});
  // useEffect(() => {
  //   onValue(getRoom, (querySnapshot: any) => {
  //     setRoom(querySnapshot.val());
  //   });
  // }, [roomId]);
  // return getRoom;
};

export const updateParticipantsInRoom = withFirebaseUpdate(async (room: any) => {
  const getRoom: any = dbRef(db, `rooms/${room.roomId}`);

  try {
    const validUsers = getParticipantsArray(room.user).filter(
      (participant: any) => participant !== undefined && participant !== null
    );

    await update(getRoom, {
      participants: validUsers,
    });
  } catch (err) {
    console.log(err);
    throw err;
  }
});

export const UpdateHostInRoom = withFirebaseUpdate(async (room: any) => {
  const getRoom: any = dbRef(db, `rooms/${room.roomId}`);
  try {
    await update(getRoom, {
      host: room.host
    });
  } catch (err) {
    console.log(err);
  }
});

export const updateLastSeenMessage = withFirebaseUpdate(async (roomId: number, userId: number) => {
  try {
    // Get the latest message ID for this room
    const messagesRef = dbRef(db, `chats/${roomId}/messages`);
    const messagesSnapshot = await get(messagesRef);
    
    if (messagesSnapshot.exists()) {
      const messages = Object.values(messagesSnapshot.val() || {});
      if (messages.length > 0) {
        // Sort messages by creation time to get the latest
        messages.sort((a: any, b: any) => {
          const aTime = a.timestamp || new Date(a.createdTime || 0).getTime();
          const bTime = b.timestamp || new Date(b.createdTime || 0).getTime();
          return bTime - aTime; // Descending order: latest first
        });

        const latestMessage = messages[0] as any;
        const latestMessageId = latestMessage?.id;
        // Store the timestamp too — the unread count compares by time, which is robust
        // even when the last-seen message predates the loaded window.
        const latestMessageTime = getMessageTime(latestMessage);

        if (latestMessageId) {
          // Update the room with the latest message ID as lastSeenMessage
          const roomRef = dbRef(db, `rooms/${roomId}`);
          const roomSnapshot = await get(roomRef);

          if (roomSnapshot.exists()) {
            const roomData = roomSnapshot.val();
            // The room's current message count = "everything read up to now".
            const seenCount = typeof roomData.messageCount === 'number' ? roomData.messageCount : 0;
            if (roomData.host?.pk === userId) {
              // User is host, update host's
              const updatedHost = {
                ...roomData.host,
                lastSeenMessage: latestMessageId,
                lastSeenTime: latestMessageTime,
                lastSeenCount: seenCount
              };

              await update(roomRef, {
                host: updatedHost
              });
            } else if (roomData.participants) {
              // User is participant, update participant's lastSeenMessage
              const updatedParticipants = roomData.participants.map((participant: any) => {
                if (participant.pk === userId) {
                  return {
                    ...participant,
                    lastSeenMessage: latestMessageId,
                    lastSeenTime: latestMessageTime,
                    lastSeenCount: seenCount
                  };
                }
                return participant;
              });

              await update(roomRef, {
                participants: updatedParticipants
              });
            }
          }
        }
      }
    }
  } catch (err) {
    console.log('Error updating lastSeenMessage:', err);
    throw err;
  }
});

export const deleteParticipants = withFirebaseUpdate(async (data: any) => {
  const participantRef: any = dbRef(db, `rooms/${data.roomId}/participants/${data.participantId}`);
  
  try {  
    await remove(participantRef);
  } catch (err) {
    console.log(err);
    throw err;
  }
});

export const removeUserFromAllFirebaseRooms = async (userPk: number) => {
  try {
    const roomsRef = dbRef(db, 'rooms');
    const snapshot = await get(roomsRef);
    
    if (!snapshot.exists()) {
      return;
    }
    
    const rooms = snapshot.val();
    const updatePromises: Promise<void>[] = [];
    
    for (const roomId in rooms) {
      const room = rooms[roomId];
      if (!room.participants) continue;
      
      // Handle both array and object formats
      let participants: any[] = [];
      if (Array.isArray(room.participants)) {
        participants = room.participants;
      } else {
        participants = Object.values(room.participants).filter((p: any) => p != null);
      }
      
      // Filter out the user
      const filteredParticipants = participants.filter((p: any) => p && p.pk !== userPk);
      
      // Only update if user was actually in the room
      if (filteredParticipants.length !== participants.length) {
        const roomRef = dbRef(db, `rooms/${roomId}`);
        updatePromises.push(
          update(roomRef, { participants: filteredParticipants }).catch((err) => {
            console.error(`Failed to remove user from room ${roomId}:`, err);
          })
        );
      }
    }
    
    // Wait for all updates to complete
    await Promise.all(updatePromises);
  } catch (err) {
    console.error('Error removing user from Firebase rooms:', err);
    // Don't throw - allow account deletion to proceed even if Firebase cleanup fails
  }
};

export const deleteRoom = withFirebaseUpdate(async (room: any) => {
  const removeRoom: any = dbRef(db, `rooms/${room.roomId}`);
  try {
    await update(removeRoom, {
      is_deleted: true
    });
  } catch (err) {
    console.log(err);
    throw err;
  }
});

/**
 * Keep exactly one Welcome-topic room flagged `is_public: true`.
 * Duplicate topics created by other users are cleared.
 */
export const ensureCanonicalPublicRoomFlag = withFirebaseUpdate(async (rooms?: any[]) => {
  let list = rooms;
  if (!Array.isArray(list) || list.length === 0) {
    const snapshot = await get(dbRef(db, 'rooms'));
    list = [];
    if (snapshot.exists()) {
      snapshot.forEach((doc: any) => {
        const data = doc.val();
        if (data && data.is_deleted !== true) {
          list!.push(data);
        }
      });
    }
  }

  const welcomeRooms = list.filter(
    (r: any) => r && r.is_deleted !== true && isWelcomePublicTopic(r.topic)
  );
  if (welcomeRooms.length === 0) return null;

  const canonical = pickCanonicalPublicRoom(welcomeRooms);
  if (!canonical?.pk) return null;

  await Promise.all(
    welcomeRooms.map(async (r: any) => {
      const shouldBePublic = Number(r.pk) === Number(canonical.pk);
      if (Boolean(r.is_public) === shouldBePublic) return;
      try {
        await update(dbRef(db, `rooms/${r.pk}`), { is_public: shouldBePublic });
      } catch (err) {
        console.error('Failed to sync is_public on room', r.pk, err);
      }
    })
  );

  return { ...canonical, is_public: true };
});

/**
 * Add the current user to a public room's participants (no host approval).
 * Django first (source of record), then Firebase — mirrors private accept flow.
 */
export const ensureUserInPublicRoom = withFirebaseUpdate(async (room: any, user: any, allRooms?: any[]) => {
  if (!room?.pk || !user?.pk) {
    return false;
  }
  if (!isPublicRoom(room, allRooms)) {
    return false;
  }

  const { access, refresh, ...userData } = user;
  const roomRef = dbRef(db, `rooms/${room.pk}`);

  try {
    await api.joinPublicRoomRequest({ room_id: String(room.pk) });

    if (Array.isArray(allRooms) && allRooms.length > 0) {
      await ensureCanonicalPublicRoomFlag(allRooms);
    } else if (isWelcomePublicTopic(room.topic)) {
      await ensureCanonicalPublicRoomFlag();
    }

    const snapshot = await get(roomRef);
    if (!snapshot.exists()) {
      return false;
    }

    const roomData = snapshot.val();
    const participants = getParticipantsArray(roomData.participants).filter((p: any) => p != null);
    const joined = participants.some((participant: any) => participant.pk === userData.pk);

    const patch: Record<string, unknown> = {};
    if (roomData.is_public !== true) {
      patch.is_public = true;
    }
    if (!joined) {
      patch.participants = [...participants, userData];
    }

    if (Object.keys(patch).length > 0) {
      await update(roomRef, patch);
    }

    return true;
  } catch (error) {
    console.error('Error ensuring user joined the public room:', error);
    throw error;
  }
});

export const ensureUserInWelcomeRoom = withFirebaseUpdate(async (user: any) => {
  const canonical = await ensureCanonicalPublicRoomFlag();
  if (!canonical?.pk) {
    console.warn('No Welcome public room found by topic');
    return false;
  }
  return ensureUserInPublicRoom(canonical, user, [canonical]);
});

/** Stable identity so `chat` doesn't change reference on every render of an empty room. */
const EMPTY_MESSAGES: any[] = [];

/**
 * Messages for a room, served from the shared cache (see messagesStore).
 *
 * Cache-first: if the room is already cached — which it is for every room shown in the
 * sidebar, since ChatParticipants subscribes for unread counts — `chat` is populated on
 * the very first render, so switching rooms is instant and no spinner is shown. Reading
 * `getRoomEntry(pk)` also means another room's messages are structurally unreachable.
 */
export const useGetMessages = (pk?: number) => {
  const [, forceRender] = useReducer((count: number) => count + 1, 0);

  useEffect(() => {
    if (!pk) return undefined;
    return subscribeMessages(pk, forceRender);
  }, [pk]);

  const entry = getRoomEntry(pk);

  const loadOlderMessages = useCallback(
    () => (pk ? loadOlderMessagesForRoom(pk) : Promise.resolve()),
    [pk]
  );

  return {
    chat: entry?.list ?? EMPTY_MESSAGES,
    // Only true the first time a room is ever opened; cached rooms render immediately.
    isLoading: !entry?.initialized,
    // Pagination properties for dynamic loading
    hasMore: entry?.hasMore ?? true,
    isLoadingMore: entry?.isLoadingMore ?? false,
    loadOlderMessages,
    isInitialized: entry?.initialized ?? false,
  };
};

// Custom hook for thread messages
export const useGetThreadMessages = (roomId: number, parentMessageId: string) => {
  const [threads, setThreads] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const isInitializedRef = useRef(false);
  const listenersRef = useRef<any>(null);

  // Memoize the sorted threads array
  const sortedThreads = useMemo(() => {
    return threads.sort((a, b) => {
      const timeA = getMessageTime(a);
      const timeB = getMessageTime(b);
      return timeA - timeB; // Ascending order: oldest first
    });
  }, [threads]);

  useEffect(() => {
    if (!roomId || !parentMessageId) return;

    const threadsRef = dbRef(db, `chats/${roomId}/messages/${parentMessageId}/threads`);
    setIsLoading(true);
    isInitializedRef.current = false;

    // Clean up any existing listener
    if (listenersRef.current) {
      off(threadsRef, 'value', listenersRef.current);
    }

    // Set up real-time listener
    const handleValueChange = onValue(threadsRef, (snapshot) => {
      const threadsData = snapshot.val();
      if (threadsData) {
        const threadsArray = Array.isArray(threadsData) ? threadsData : Object.values(threadsData);
        setThreads(threadsArray);
      } else {
        setThreads([]);
      }
      setIsLoading(false);
      isInitializedRef.current = true;
    });

    // Store listener for cleanup
    listenersRef.current = handleValueChange;

    // Cleanup listener on unmount
    return () => {
      if (listenersRef.current) {
        off(threadsRef, 'value', listenersRef.current);
      }
      listenersRef.current = null;
      isInitializedRef.current = false;
    };
  }, [roomId, parentMessageId]);

  return { 
    threads: sortedThreads, 
    isLoading,
    isInitialized: isInitializedRef.current
  };
};

const user = JSON.parse(localStorage.getItem("user") as string);
export const useGetRooms = () => {
  const [rooms, setRooms] = useState<any>([]);
  const staleClearedRef = useRef(false);
  
  useEffect(() => {
    const roomsRef = dbRef(db, "rooms");
    
    const unsubscribe = onValue(roomsRef, (querySnapshot: any) => {
      let allRooms: any = [];
      querySnapshot.forEach((doc: any) => {
        const data = doc.val();
        if (data.is_deleted !== true) {
          allRooms.push({
            ...data,
            distance: getDistance(
              user?.latitude,
              user?.longitude,
              data.host?.latitude,
              data.host?.longitude
            ),
          });
        }
      });
      allRooms = sortRoomsByDistance(allRooms);
      setRooms(allRooms);

      // One-shot: reset VIDEO rooms abandoned after host tab-close / crash.
      if (!staleClearedRef.current && allRooms.length > 0) {
        staleClearedRef.current = true;
        clearStaleVideoCalls(allRooms).catch((err: any) => {
          staleClearedRef.current = false;
          if (err?.code === 'INVALID_USER_DATA') return;
          console.error('Failed to clear stale video calls:', err);
        });
      }
    });

    // Cleanup function to unsubscribe when component unmounts
    return () => {
      unsubscribe();
    };
  }, []);
  
  return rooms;
};

export const sendFCMMessage = withFirebaseUpdate(async (message: any) => {
  const roomId = message.roomId;
  try {
    // Single root multi-path update so the message write and the room's monotonic
    // `messageCount` bump are atomic — the counter can never drift from the messages.
    // `increment()` is a server-side atomic op, so concurrent sends both count.
    const hasAttachments = Array.isArray(message.attachments) && message.attachments.length > 0;
    const rootUpdate: Record<string, any> = {
      [`chats/${roomId}/messages/${message.id}`]: {
        id: message.id,
        sender: message.sender,
        receiver: message.receiver,
        message: message.message,
        time: message.time,
        is_delete: message.is_delete,
        attachments: message.attachments,
        threads: message.threads,
        createdTime: moment().format(),  // Client time for UI display
        timestamp: serverTimestamp()     // Server timestamp for consistent ordering
      },
      [`rooms/${roomId}/messageCount`]: increment(1),
    };
    // Signals Chat Info to re-list Storage even when this message is outside a
    // peer's loaded message window (attachmentSig alone can't see it).
    if (hasAttachments) {
      rootUpdate[`rooms/${roomId}/mediaVersion`] = increment(1);
    }
    await update(dbRef(db), rootUpdate);
  } catch (err) {
    console.log(err);
  }
});

export const sendThreadMessage = withFirebaseUpdate(async (message: any) => {
  const roomId = message.roomId;
  
  try {
    // Use get() for one-time read instead of onValue listener
    const messagesRef = dbRef(db, `chats/${roomId}/messages`);
    const messagesSnapshot = await get(messagesRef);
    
    // Find the parent message
    let findMsg: any = {};
    messagesSnapshot.forEach((doc: any) => {
      if (doc.val().id === message.parentId) {
        findMsg = doc.val();
      }
    });
    
    // Check if parent message exists
    if (!findMsg || Object.keys(findMsg).length === 0) {
      throw new Error('Parent message not found');
    }
    
    const isThreadsExist = findMsg.hasOwnProperty('threads');
    let length = 0;
    
    if (isThreadsExist) {
      // Use get() for one-time read of threads
      const threadsRef = dbRef(db, `chats/${roomId}/messages/${message.parentId}/threads`);
      const threadsSnapshot = await get(threadsRef);
      const obj = threadsSnapshot.val();
      
      // Handle both array and object formats
      if (Array.isArray(obj)) {
        length = obj.length;
      } else if (obj && typeof obj === 'object') {
        length = Object.keys(obj).length;
      } else {
        length = 0;
      }
    }
    
    if (length === 0) {
      // Create threads array with first message
      const messages: any = dbRef(db, `chats/${roomId}/messages/${message.parentId}`);
      await update(messages, {
        threads: [
          {
            id: message.id,
            sender: message.sender,
            receiver: message.receiver,
            message: message.message,
            time: message.time,
            is_delete: message.is_delete,
            attachments: message.attachments,
            createdTime: moment().format(),
            timestamp: serverTimestamp()
          }
        ]
      });
    } else {
      // Append to existing threads
      // Use push() for arrays or set with generated key for objects
      const threadsRef = dbRef(db, `chats/${roomId}/messages/${message.parentId}/threads`);
      const threadsSnapshot = await get(threadsRef);
      const threadsData = threadsSnapshot.val();
      
      const newThread = {
        id: message.id,
        sender: message.sender,
        receiver: message.receiver,
        message: message.message,
        time: message.time,
        is_delete: message.is_delete,
        attachments: message.attachments,
        createdTime: moment().format(),
        timestamp: serverTimestamp()
      };
      
      if (Array.isArray(threadsData)) {
        // If threads is an array, append to it
        await update(threadsRef, {
          [length]: newThread
        });
      } else {
        // If threads is an object, add with a new key
        const newKey = length.toString();
        await update(threadsRef, {
          [newKey]: newThread
        });
      }
    }

    // Same mediaVersion signal as main-chat sends: thread attachments live under
    // the parent message, which may be outside another user's loaded window.
    if (Array.isArray(message.attachments) && message.attachments.length > 0) {
      await update(dbRef(db, `rooms/${roomId}`), { mediaVersion: increment(1) });
    }
  } catch (err) {
    console.error('Error sending thread message:', err);
    throw err; // Re-throw to allow component error handling
  }
});

export const deleteMessage = withFirebaseUpdate(async (path: any) => {
  const message: any = dbRef(db, `chats/${path}`);
  try {
    await remove(message);
    // Keep messageCount in sync. `path` is "{roomId}/messages/{id}"; only decrement for
    // actual message deletions (not the participants path some callers pass).
    const [roomId, section] = String(path).split('/');
    if (roomId && section === 'messages') {
      await update(dbRef(db, `rooms/${roomId}`), { messageCount: increment(-1) });
    }
  } catch (err) {
    console.log(err);
    throw err;
  }
});

export const deleteImage = async (path: any) => {
  const desertRef = storageRef(storage, `${path}`);
  try {
    // Awaited so callers can rely on the file actually being gone before they act
    // (e.g. re-listing the room's media) — otherwise a listAll can race the delete
    // and read the object back.
    await deleteObject(desertRef);
    // Drop the cached download URL, so a stale (now 403) URL is never served.
    invalidateAttachmentUrl(`${path}`);
  } catch (error) {
    console.warn('Failed to delete attachment from storage:', error);
  }
};

export const deleteThreadMessage = withFirebaseUpdate(async (roomId: string, parentMessageId: string, threadIndex: number) => {
  try {
    // Get current threads array
    const threadsRef = dbRef(db, `chats/${roomId}/messages/${parentMessageId}/threads`);
    const snapshot = await get(threadsRef);
    const threads = snapshot.val() || [];
    
    // Remove the thread at the specified index
    const updatedThreads = threads.filter((_: any, index: number) => index !== threadIndex);
    
    // Update the threads array
    await set(threadsRef, updatedThreads);
    console.log('Thread message deleted successfully');
  } catch (error) {
    console.error('Error deleting thread message:', error);
    throw error;
  }
});

/**
 * `roomId` is unused: the room folder is derived from the attachment id
 * ("778-dR2KdtpbKSEjxKf" -> folder "778"). Kept for call-site compatibility.
 *
 * Backed by attachmentUrlCache, so repeated mounts (every room switch) don't re-issue a
 * getDownloadURL round-trip. Misses are negative-cached; real errors still surface.
 */
export const getImages = async (roomId: string, attachmentId: string) => {
  const images: any = [];
  try {
    const url = await resolveAttachmentUrl(attachmentPath(attachmentId));
    if (url) {
      images[0] = url;
    } else {
      console.warn(`Attachment file not found: ${attachmentId}`);
    }
  } catch (err: any) {
    console.error('Error getting image:', err);
  }
  return images;
};

export const uploadImage = async (message: any, file: any, onProgress?: (percent: number) => void) => {
  try {
    // Persist the original filename as custom metadata so views that read from
    // Storage (e.g. the Chat Info media gallery) can show the real name without
    // needing the originating chat message to be loaded.
    const task = uploadBytesResumable(
      storageRef(storage, `/${message.roomId}/${file.id}`),
      file.file,
      { customMetadata: { originalName: file.name || '' } }
    );
    // Report upload progress (0–100) so the footer can show a progress bar.
    if (onProgress) {
      task.on('state_changed', (snapshot) => {
        onProgress(snapshot.totalBytes ? Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100) : 0);
      });
    }
    return await task; // resolves on completion
  } catch (err) {}
  return '';
};

export const uploadIntroVideo = async (roomId: string, file: File) => {
  try {
    // Validate file size as a safeguard (should already be validated in UI)
    if (file.size > MAX_INTRO_VIDEO_SIZE) {
      throw new Error(`Video file size exceeds the maximum allowed size of ${MAX_INTRO_VIDEO_SIZE / (1024 * 1024)}MB`);
    }
    
    const videoId = `intro-video-${Date.now()}`;
    const videoRef = storageRef(storage, `/${roomId}/${videoId}`);
    
    const uploadTask = uploadBytesResumable(videoRef, file, {
      contentType: file.type || 'video/mp4',
      cacheControl: 'public, max-age=31536000',
    });
    
    // Monitor upload progress
    uploadTask.on('state_changed', 
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
      },
      (error) => {
        console.error('Upload error during progress:', error);
      }
    );
    
    // Wait for upload to complete using the task's promise
    await uploadTask;
    
    const videoUrl = await getDownloadURL(videoRef);
    
    // Return videoRef so caller can delete it if backend update fails
    return { videoUrl, videoId, videoRef };
  } catch (err: any) {
    console.error('Error uploading intro video:', err);
    throw err;
  }
};

export const updateCallStatus = withFirebaseUpdate(async (room: any) => {
  const getRoom: any = dbRef(db, `rooms/${room.roomId}`);
  try {
    await update(getRoom, {
      call_status: room.status
    });
  } catch (err) {
    console.log(err);
    throw err;
  }
});

export const useGetCallStatus = (roomId: any) => {
  const [room, setRoom] = useState<any>({});
  
  useEffect(() => {
    if (!roomId) return;
    
    const getRoom: any = dbRef(db, `rooms/${roomId}`);
    
    // Set up the listener
    const unsubscribe = onValue(getRoom, (querySnapshot: any) => {
      setRoom(querySnapshot.val());
    });
    
    // Cleanup listener on unmount or when roomId changes
    return () => {
      unsubscribe();
    };
  }, [roomId]);
  
  return room;
};

export const sendJoinRequest = withFirebaseUpdate(async (request: any) => {
  const roomId = request.roomId;
  
  try {    
    const requestsRef = dbRef(db, `requests/${roomId}/requests`);
    
    // Check if there's already a pending request from this user
    const snapshot = await get(requestsRef);
    const existingRequests = snapshot.val() || {};
    
    // Check if user already has a pending request (ignore cleared/empty nodes)
    const userHasPendingRequest = Object.values(existingRequests).some(
      (req: any) =>
        req?.user?.pk != null &&
        String(req.user.pk) === String(request.user.pk)
    );
    
    if (userHasPendingRequest) {
      // User already has a pending request, throw specific error
      const error: any = new Error('DUPLICATE_REQUEST');
      error.code = 'DUPLICATE_REQUEST';
      throw error;
    }
    
    // No pending request found, send new request
    const newRequestRef = dbRef(db, `requests/${roomId}/requests/${request.id}`);
    await set(newRequestRef, {
      id: request.id,
      user: request.user,
      time: request.time,
    });

    markJoinRequestPending(
      request.user.pk,
      roomId,
      request.roomTopic || 'this room'
    );

    // Call the backend API to send email notification
    await api.sendJoinRequestEmail({ room_id: String(roomId) });
  } catch (err) {
    console.log('Error sending join request:', err);
    throw err; // Re-throw to handle in UI
  }
});

export const deleteRequest = withFirebaseUpdate(async (path: any) => {
  const request: any = dbRef(db, `requests/${path}`);
  try {
    await remove(request);
  } catch (err) {
    console.log(err);
    throw err;
  }
});

// ---------------------------------------------------------------------------
// Live video call join approval (host ACCEPT / REJECT on /call)
// Path: call_join_requests/{roomId}/{userPk}
// ---------------------------------------------------------------------------

export type CallJoinRequestStatus = 'pending' | 'approved' | 'rejected';

export type CallJoinRequest = {
  user: {
    pk: number | string;
    name?: string;
    email?: string;
    profile?: string | null;
  };
  status: CallJoinRequestStatus;
  requestedAt: number;
};

export const requestCallJoin = withFirebaseUpdate(async (roomId: number | string, user: any) => {
  if (roomId == null || roomId === '' || user?.pk == null) {
    throw new Error('requestCallJoin requires roomId and user.pk');
  }
  const path = `call_join_requests/${roomId}/${user.pk}`;
  const existing = await get(dbRef(db, path));
  if (existing.exists()) {
    const val = existing.val() as CallJoinRequest;
    if (val?.status === 'pending' || val?.status === 'approved') {
      return val;
    }
  }
  const payload: CallJoinRequest = {
    user: {
      pk: user.pk,
      name: user.name,
      email: user.email,
      profile: user.profile ?? null,
    },
    status: 'pending',
    requestedAt: Date.now(),
  };
  await set(dbRef(db, path), payload);
  return payload;
});

export const respondCallJoinRequest = withFirebaseUpdate(async (
  roomId: number | string,
  userPk: number | string,
  status: 'approved' | 'rejected'
) => {
  const path = `call_join_requests/${roomId}/${userPk}`;
  const snap = await get(dbRef(db, path));
  if (!snap.exists()) return;
  await update(dbRef(db, path), { status });
});

export const clearCallJoinRequest = withFirebaseUpdate(async (
  roomId: number | string,
  userPk: number | string
) => {
  await remove(dbRef(db, `call_join_requests/${roomId}/${userPk}`));
});

export const clearAllCallJoinRequests = withFirebaseUpdate(async (roomId: number | string) => {
  await remove(dbRef(db, `call_join_requests/${roomId}`));
});

/** Host: listen for pending (and all) call join requests. */
export const subscribeCallJoinRequests = (
  roomId: number | string,
  onRequests: (requests: CallJoinRequest[]) => void
): (() => void) => {
  const requestsRef = dbRef(db, `call_join_requests/${roomId}`);
  return onValue(requestsRef, (snapshot) => {
    const val = snapshot.val() || {};
    const list: CallJoinRequest[] = Object.values(val).filter(
      (r: any) => r?.user?.pk != null && r?.status
    ) as CallJoinRequest[];
    list.sort((a, b) => (a.requestedAt || 0) - (b.requestedAt || 0));
    onRequests(list);
  });
};

/** Guest: listen to own call join request status. */
export const subscribeOwnCallJoinRequest = (
  roomId: number | string,
  userPk: number | string,
  onRequest: (request: CallJoinRequest | null) => void
): (() => void) => {
  const reqRef = dbRef(db, `call_join_requests/${roomId}/${userPk}`);
  return onValue(reqRef, (snapshot) => {
    onRequest(snapshot.exists() ? (snapshot.val() as CallJoinRequest) : null);
  });
};

// Function to check if room has location data
export const hasLocationData = (room: any) => {
  return room && room.city && room.country && 
    room.city !== "Unknown" && room.country !== "Unknown";
};

// Function to get room location with fallback
export const getRoomLocation = (room: any) => {
  if (hasLocationData(room)) {
    return {
      city: room.city,
      country: room.country
    };
  }
  return {
    city: "Unknown",
    country: "Unknown"
  };
};


